import { Injectable, NotFoundException, ConflictException, Optional } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import {
  ApproveMerchantRequest,
  DELETED_MERCHANT_RETENTION_DAYS,
  MERCHANT_LIST_SORTS,
  MerchantListQuery,
  MerchantListSort,
  UpdateMerchantSettingsRequest,
} from '@cart/shared-types';
import { EmailService } from '../notifications/email.service';

@Injectable()
export class PlatformService {
  constructor(
    private readonly prisma: PrismaService,
    @Optional()
    private readonly emailService?: EmailService
  ) {}

  // ── Permissions ───────────────────────────────────────────────────────────

  async listPermissions(scope?: string) {
    return this.prisma.platformPermission.findMany({
      where: {
        isActive: true,
        ...(scope ? { scope } : {}),
      },
      orderBy: [{ scope: 'asc' }, { sortOrder: 'asc' }],
      select: {
        id: true,
        code: true,
        name: true,
        module: true,
        scope: true,
        category: true,
        description: true,
        isActive: true,
        sortOrder: true,
      },
    });
  }

  // ── Roles ─────────────────────────────────────────────────────────────────

  async listRoles(scope?: string) {
    return this.prisma.platformRole.findMany({
      where: {
        isActive: true,
        ...(scope ? { scope } : {}),
      },
      orderBy: { sortOrder: 'asc' },
      select: {
        id: true,
        code: true,
        name: true,
        scope: true,
        description: true,
        isSystem: true,
        isActive: true,
        sortOrder: true,
        rolePermissions: {
          where: { isActive: true },
          select: {
            permission: {
              select: { id: true, code: true, name: true, scope: true, category: true },
            },
          },
        },
      },
    });
  }

  async getRoleWithPermissions(roleId: string) {
    const role = await this.prisma.platformRole.findUnique({
      where: { id: roleId },
      include: {
        rolePermissions: {
          where: { isActive: true },
          include: {
            permission: {
              select: { id: true, code: true, name: true, scope: true, category: true },
            },
          },
        },
      },
    });

    if (!role) throw new NotFoundException(`Role ${roleId} not found`);
    return role;
  }

  async assignPermissionToRole(roleId: string, permissionId: string, actorId: string) {
    const [role, perm] = await Promise.all([
      this.prisma.platformRole.findUnique({ where: { id: roleId }, select: { id: true, isSystem: true } }),
      this.prisma.platformPermission.findUnique({ where: { id: permissionId }, select: { id: true } }),
    ]);

    if (!role) throw new NotFoundException(`Role ${roleId} not found`);
    if (!perm) throw new NotFoundException(`Permission ${permissionId} not found`);
    if (role.isSystem) throw new ConflictException('Cannot modify system role permissions via API');

    return this.prisma.platformRolePermission.upsert({
      where: { roleId_permissionId: { roleId, permissionId } },
      create: { roleId, permissionId, isActive: true, createdBy: actorId, updatedBy: actorId },
      update: { isActive: true, updatedBy: actorId },
    });
  }

  async revokePermissionFromRole(roleId: string, permissionId: string, actorId: string) {
    const existing = await this.prisma.platformRolePermission.findUnique({
      where: { roleId_permissionId: { roleId, permissionId } },
      select: { id: true },
    });

    if (!existing) throw new NotFoundException('Role-permission mapping not found');

    return this.prisma.platformRolePermission.update({
      where: { roleId_permissionId: { roleId, permissionId } },
      data: { isActive: false, updatedBy: actorId },
    });
  }

  // ── Merchants (Organizations) ─────────────────────────────────────────────

  // ── Merchant list ─────────────────────────────────────────────────────────

  /** Matches `/products`: a caller cannot ask for an unbounded page. */
  private static readonly MAX_PAGE_SIZE = 100;
  private static readonly DEFAULT_PAGE_SIZE = 10;

  private storeSelect() {
    return {
      id: true,
      name: true,
      slug: true,
      city: true,
      state: true,
      isActive: true,
      deactivatedAt: true,
      // The subdomain the merchant's storefront answers on. Only the primary
      // one — a store can hold several domains, but the list shows the address
      // an operator would actually open.
      domains: {
        where: { isPrimary: true, isActive: true },
        select: { domain: true },
        take: 1,
      },
    };
  }

  /**
   * Search matches a merchant on its own fields **or on any of its stores**.
   *
   * Note this filters MERCHANTS only. The stores returned under a matched
   * merchant are always its full list — filtering those by the same term would
   * make the expanded row under-report how many stores a merchant has, which
   * reads as data loss rather than as a filter.
   */
  private searchWhere(search?: string) {
    const q = search?.trim();
    if (!q) return {};

    const contains = { contains: q, mode: 'insensitive' as const };
    return {
      OR: [
        { name: contains },
        { email: contains },
        { city: contains },
        { state: contains },
        {
          stores: {
            some: {
              deletedAt: null,
              OR: [
                { name: contains },
                { city: contains },
                { state: contains },
                { domains: { some: { domain: contains } } },
              ],
            },
          },
        },
      ],
    };
  }

  /**
   * `deleted` is a view of its own, not a status within the main list.
   *
   * Everything except `deleted` excludes soft-deleted merchants; `deleted`
   * shows only those, and only within the retention window. Past the window the
   * row still exists — it simply stops being listed. See
   * docs/18-merchants-stores-merge-plan.md §4.
   */
  private filterWhere(filter?: string) {
    if (filter === 'deleted') {
      const cutoff = new Date(Date.now() - DELETED_MERCHANT_RETENTION_DAYS * 24 * 60 * 60 * 1000);
      return { deletedAt: { not: null, gte: cutoff } };
    }

    const base = { deletedAt: null };
    if (filter === 'active') return { ...base, isActive: true, suspendedAt: null };
    if (filter === 'suspended') return { ...base, suspendedAt: { not: null } };
    if (filter === 'pending') return { ...base, approvalStatus: 'pending' };
    return base;
  }

  /**
   * Ordering, always with `id` as a tiebreaker.
   *
   * Without the tiebreaker, rows that share a sort value have no defined order
   * between pages, so the same merchant can appear on page 1 and again on
   * page 2 while another is never shown at all. That is invisible in testing
   * with a handful of rows and obvious in production.
   *
   * `plan` sorts **alphabetically** (basic, free, premium), not by tier
   * (free, basic, premium). Prisma cannot express a CASE ordering, and doing it
   * properly means hand-writing the paginated search query in raw SQL. Grouping
   * plans together is most of the value; if tier order is wanted later, that is
   * the trade to make. Documented rather than left to be discovered.
   */
  private orderBy(sort: MerchantListSort, dir: 'asc' | 'desc') {
    if (sort === 'storeCount') {
      return [{ stores: { _count: dir } }, { id: 'asc' as const }];
    }
    // Nulls last in both directions: Postgres leads with NULLs on ASC, so
    // sorting by email would otherwise open with every merchant that has none.
    if (sort === 'email') {
      return [{ email: { sort: dir, nulls: 'last' as const } }, { id: 'asc' as const }];
    }
    return [{ [sort]: dir }, { id: 'asc' as const }];
  }

  async listMerchants(query: MerchantListQuery = {}) {
    const page = Math.max(1, Math.trunc(Number(query.page) || 1));
    const requested = Math.trunc(Number(query.limit) || PlatformService.DEFAULT_PAGE_SIZE);
    const limit = Math.min(Math.max(1, requested), PlatformService.MAX_PAGE_SIZE);

    // An unrecognised sort or direction falls back to the default rather than
    // erroring — a bad query string should not break the page.
    const sort: MerchantListSort = MERCHANT_LIST_SORTS.includes(query.sort as MerchantListSort)
      ? (query.sort as MerchantListSort)
      : 'createdAt';
    const dir: 'asc' | 'desc' = query.dir === 'asc' ? 'asc' : 'desc';

    const where = { ...this.filterWhere(query.filter), ...this.searchWhere(query.search) };

    // Counts for the filter chips and header stats.
    //
    // These CANNOT be derived from the returned page: with ten rows per page
    // every stat would read "10". They also deliberately ignore the active
    // filter (so the chips always show the full picture) while still honouring
    // the search, so counts describe what the operator is looking at.
    const searchOnly = this.searchWhere(query.search);
    const countFor = (filter: string) =>
      this.prisma.organization.count({ where: { ...this.filterWhere(filter), ...searchOnly } });

    const [total, items, all, pending, active, suspended, deleted] = await this.prisma.$transaction([
      this.prisma.organization.count({ where }),
      this.prisma.organization.findMany({
        where,
        orderBy: this.orderBy(sort, dir),
        skip: (page - 1) * limit,
        take: limit,
        select: {
          id: true,
          name: true,
          slug: true,
          email: true,
          phone: true,
          businessType: true,
          gstin: true,
          city: true,
          state: true,
          plan: true,
          planExpiresAt: true,
          storageCapBytes: true,
          maxImageSizeBytes: true,
          maxImagesPerProduct: true,
          planVerifiedAt: true,
          suspendedAt: true,
          suspendedBy: true,
          suspensionReason: true,
          approvalStatus: true,
          approvedAt: true,
          approvedBy: true,
          rejectedAt: true,
          rejectedBy: true,
          rejectionReason: true,
          maxStores: true,
          isActive: true,
          deletedAt: true,
          createdAt: true,
          stores: {
            where: { deletedAt: null },
            select: this.storeSelect(),
            orderBy: { createdAt: 'asc' },
          },
        },
      }),
      countFor('all'),
      countFor('pending'),
      countFor('active'),
      countFor('suspended'),
      countFor('deleted'),
    ]);

    return {
      counts: { all, pending, active, suspended, deleted },
      // `domains` is a join artefact; the client wants one `domain` per store.
      items: items.map(org => ({
        ...org,
        // BigInt is not JSON-serialisable and would throw on response.
        storageCapBytes: org.storageCapBytes == null ? null : Number(org.storageCapBytes),
        planVerifiedAt: org.planVerifiedAt?.toISOString() ?? null,
        stores: org.stores.map(({ domains, ...store }) => ({
          ...store,
          domain: domains[0]?.domain ?? null,
        })),
      })),
      total,
      page,
      limit,
      totalPages: Math.max(1, Math.ceil(total / limit)),
    };
  }

  async getMerchant(id: string) {
    const org = await this.prisma.organization.findUnique({
      where: { id },
      include: {
        stores: {
          where: { deletedAt: null },
          orderBy: { createdAt: 'asc' },
        },
      },
    });

    if (!org) throw new NotFoundException(`Merchant ${id} not found`);
    return org;
  }

  async suspendMerchant(id: string, reason: string, actorId: string) {
    await this.getMerchant(id);

    await this.prisma.$transaction([
      this.prisma.organization.update({
        where: { id },
        data: {
          isActive: false,
          suspendedAt: new Date(),
          suspendedBy: actorId,
          suspensionReason: reason,
          updatedBy: actorId,
        },
      }),
      this.prisma.store.updateMany({
        where: { organizationId: id, deletedAt: null },
        data: { isActive: false, deactivatedAt: new Date(), deactivatedBy: actorId, updatedBy: actorId },
      }),
    ]);

    return { success: true };
  }

  async activateMerchant(id: string, actorId: string) {
    await this.getMerchant(id);

    await this.prisma.$transaction([
      this.prisma.organization.update({
        where: { id },
        data: {
          isActive: true,
          suspendedAt: null,
          suspendedBy: null,
          suspensionReason: null,
          updatedBy: actorId,
        },
      }),
      this.prisma.store.updateMany({
        where: { organizationId: id, deletedAt: null },
        data: { isActive: true, deactivatedAt: null, deactivatedBy: null, updatedBy: actorId },
      }),
    ]);

    return { success: true };
  }

  // ── Delete / restore ──────────────────────────────────────────────────────

  /**
   * Closes a merchant's account.
   *
   * Distinct from suspend: a suspended merchant can still sign in and see their
   * data behind a warning banner, a deleted one cannot sign in at all. The two
   * are separated purely by which column is set — `suspendedAt` vs `deletedAt`
   * — and the auth layer reads both.
   *
   * **Destroys nothing.** Orders, invoices and audit history stay intact, which
   * is what makes `restore` a real operation rather than a promise. See
   * docs/18-merchants-stores-merge-plan.md §4.
   */
  async deleteMerchant(id: string, actorId: string) {
    const org = await this.prisma.organization.findUnique({
      where: { id },
      select: { id: true, deletedAt: true },
    });
    if (!org) throw new NotFoundException(`Merchant ${id} not found`);

    // Idempotent: deleting an already-deleted merchant is a no-op, not a 500.
    // Re-stamping deletedAt would also silently restart the retention window.
    if (org.deletedAt) return { success: true, alreadyDeleted: true };

    const now = new Date();
    await this.prisma.$transaction([
      this.prisma.organization.update({
        where: { id },
        data: { isActive: false, deletedAt: now, updatedBy: actorId },
      }),
      this.prisma.store.updateMany({
        where: { organizationId: id, deletedAt: null },
        data: { isActive: false, deactivatedAt: now, deactivatedBy: actorId, updatedBy: actorId },
      }),
      // Blocking new logins is not enough on its own: JWTs are issued at login,
      // so anyone already signed in would keep working until their token
      // expired. Revoking sessions is the actual enforcement point.
      this.prisma.userSession.updateMany({
        where: { user: { organizationId: id }, revokedAt: null },
        data: { revokedAt: now, revokedReason: 'merchant_deleted' },
      }),
    ]);

    return { success: true, alreadyDeleted: false };
  }

  /**
   * Reopens a closed account, bringing its storefronts back with it.
   *
   * Stores are reactivated to mirror the cascade on the way in — a restore that
   * left every storefront dark would look like it had not worked. Sessions are
   * NOT un-revoked: the merchant signs in again, which is the correct security
   * posture.
   */
  async restoreMerchant(id: string, actorId: string) {
    const org = await this.prisma.organization.findUnique({
      where: { id },
      select: { id: true, deletedAt: true, suspendedAt: true },
    });
    if (!org) throw new NotFoundException(`Merchant ${id} not found`);
    if (!org.deletedAt) return { success: true, alreadyActive: true };

    await this.prisma.$transaction([
      this.prisma.organization.update({
        where: { id },
        data: {
          deletedAt: null,
          // Only un-suspend if the merchant was not *also* suspended before
          // deletion — restoring must not quietly clear a separate sanction.
          isActive: org.suspendedAt ? false : true,
          updatedBy: actorId,
        },
      }),
      this.prisma.store.updateMany({
        where: { organizationId: id, deletedAt: null },
        data: org.suspendedAt
          ? { updatedBy: actorId }
          : { isActive: true, deactivatedAt: null, deactivatedBy: null, updatedBy: actorId },
      }),
    ]);

    return { success: true, alreadyActive: false };
  }

  // ── Settings ──────────────────────────────────────────────────────────────

  /**
   * Plan and quota changes after approval.
   *
   * A patch: an omitted field is left alone rather than reset. `null` is a
   * meaningful value for the quota fields — it means "no limit" — so the
   * distinction between "absent" and "explicitly null" has to be preserved.
   */
  async updateMerchantSettings(id: string, body: UpdateMerchantSettingsRequest, actorId: string) {
    const org = await this.prisma.organization.findUnique({
      where: { id },
      select: { id: true, _count: { select: { stores: { where: { deletedAt: null } } } } },
    });
    if (!org) throw new NotFoundException(`Merchant ${id} not found`);

    if (body.plan !== undefined && !['free', 'basic', 'premium'].includes(body.plan)) {
      throw new ConflictException(`Unknown plan "${body.plan}"`);
    }

    // Refused rather than accepted silently: letting maxStores drop below the
    // stores that already exist leaves the merchant in a state their own quota
    // forbids, and nothing downstream would ever reconcile it.
    if (body.maxStores != null && body.maxStores < org._count.stores) {
      throw new ConflictException(
        `This merchant already has ${org._count.stores} store(s). Raise the limit or deactivate stores first.`,
      );
    }

    for (const [field, value] of Object.entries(body)) {
      if (typeof value === 'number' && value < 0) {
        throw new ConflictException(`${field} cannot be negative`);
      }
    }

    const data: Record<string, unknown> = { updatedBy: actorId };
    if (body.plan !== undefined) data['plan'] = body.plan;
    if (body.planExpiresAt !== undefined) {
      data['planExpiresAt'] = body.planExpiresAt ? new Date(body.planExpiresAt) : null;
    }
    if (body.maxStores !== undefined) data['maxStores'] = body.maxStores;
    if (body.storageCapBytes !== undefined) data['storageCapBytes'] = body.storageCapBytes;
    if (body.maxImageSizeBytes !== undefined) data['maxImageSizeBytes'] = body.maxImageSizeBytes;
    if (body.maxImagesPerProduct !== undefined) data['maxImagesPerProduct'] = body.maxImagesPerProduct;

    /*
     * Verification is a person vouching for an account, so it records who and
     * when rather than a bare boolean. Clearing it wipes both — an account put
     * back on the provisional allowance should not still carry the name of
     * whoever once approved it.
     */
    if (body.planVerified !== undefined) {
      data['planVerifiedAt'] = body.planVerified ? new Date() : null;
      data['planVerifiedBy'] = body.planVerified ? actorId : null;
    }

    await this.prisma.organization.update({ where: { id }, data });
    return { success: true };
  }

  async approveMerchant(id: string, quotas: ApproveMerchantRequest, actorId: string) {
    const org = await this.getMerchant(id);
    if (org.approvalStatus === 'approved') throw new ConflictException('Merchant is already approved');

    const updated = await this.prisma.organization.update({
      where: { id },
      data: {
        approvalStatus: 'approved',
        approvedAt: new Date(),
        approvedBy: actorId,
        rejectedAt: null,
        rejectedBy: null,
        rejectionReason: null,
        maxStores: quotas.maxStores,
        storageCapBytes: quotas.storageCapBytes != null ? BigInt(quotas.storageCapBytes) : undefined,
        maxImageSizeBytes: quotas.maxImageSizeBytes,
        maxImagesPerProduct: quotas.maxImagesPerProduct,
        updatedBy: actorId,
      },
    });

    await this.emailService?.sendTemplated('merchant_approved', updated.email ?? '', {
      customerName: updated.name,
      businessName: updated.name,
      plan: updated.plan,
    });

    return { success: true };
  }

  async rejectMerchant(id: string, reason: string, actorId: string) {
    const org = await this.getMerchant(id);
    if (org.approvalStatus === 'approved') throw new ConflictException('Cannot reject an already-approved merchant');

    const updated = await this.prisma.organization.update({
      where: { id },
      data: {
        approvalStatus: 'rejected',
        rejectedAt: new Date(),
        rejectedBy: actorId,
        rejectionReason: reason,
        updatedBy: actorId,
      },
    });

    await this.emailService?.sendTemplated('merchant_rejected', updated.email ?? '', {
      customerName: updated.name,
      businessName: updated.name,
      rejectionReason: reason,
    });

    return { success: true };
  }

  // Store id -> {name, domain} lookup with no organization scoping — unlike
  // merchant.service.ts's getOverview() (which only resolves the CALLER's
  // own org's stores), this resolves ANY store, for platform-wide surfaces
  // (e.g. admin's Support Center linking a Service Request back to whatever
  // store it came from, regardless of which merchant owns it).
  async getStore(storeId: string) {
    const store = await this.prisma.store.findUnique({
      where: { id: storeId },
      select: {
        id: true, name: true,
        domains: { where: { isPrimary: true }, select: { domain: true }, take: 1 },
      },
    });

    if (!store) throw new NotFoundException(`Store ${storeId} not found`);

    return { id: store.id, name: store.name, domain: store.domains[0]?.domain ?? null };
  }

  async toggleStore(storeId: string, active: boolean, actorId: string) {
    const store = await this.prisma.store.findUnique({ where: { id: storeId }, select: { id: true } });
    if (!store) throw new NotFoundException(`Store ${storeId} not found`);

    return this.prisma.store.update({
      where: { id: storeId },
      data: {
        isActive: active,
        deactivatedAt: active ? null : new Date(),
        deactivatedBy: active ? null : actorId,
        updatedBy: actorId,
      },
    });
  }

  // ── Permissions for auth ──────────────────────────────────────────────────

  async getPermissionCodesForRole(roleCode: string): Promise<string[]> {
    const role = await this.prisma.platformRole.findUnique({
      where: { code: roleCode },
      select: {
        rolePermissions: {
          where: { isActive: true },
          select: { permission: { select: { code: true } } },
        },
      },
    });

    if (!role) return [];
    return role.rolePermissions.map((rp) => rp.permission.code);
  }
}
